import SimpleModal from 'components/SimpleModal';
import { Box, Typography } from '@mui/material';
import { deleteRealms } from 'thunk';
import { useAppDispatch } from 'redux/hooks';
import { checkIsSandboxDestroyable } from 'utils';
import { RealmTypes } from 'RealmModule/constants/realm';
import { Realm } from 'types';
import { Tenant } from 'TenantModule/types';
import useSandboxes from 'SandboxModule/hooks/useSandboxes';
import { useNotification } from 'hooks/useNotification';

type DestroyRealmsModalProps = {
  onClose: VoidFunction;
  realmIds: Realm['realm_id'][];
  onDestroyRealmSuccess: VoidFunction;
  realmType: RealmTypes;
  /**
   * Set when the action is triggered from the Archived tab, where the realm has
   * already been archived and this only re-runs the resource teardown that
   * stalled. The "non-recoverable" framing of the first destroy does not apply.
   */
  isRetry?: boolean;
};

const DESTROY_REASON = 'Sandbox/Subscription destroyed by user';
const RETRY_DESTROY_REASON = 'Retry destroy for partially removed realm';

const propsByRealmType = {
  [RealmTypes.sandbox]: {
    plural: 'Sandboxes',
    singular: 'Sandbox',
  },
  [RealmTypes.subscription]: {
    plural: 'Subscriptions',
    singular: 'Subscription',
  },
};

export const DestroyRealmsModal = ({
  onClose,
  realmIds,
  onDestroyRealmSuccess,
  realmType,
  isRetry = false,
}: DestroyRealmsModalProps) => {
  const dispatch = useAppDispatch();
  const { plural, singular } = propsByRealmType[realmType];
  const { getSandbox } = useSandboxes();
  const { showSuccess, showError } = useNotification();

  const onConfirm = () => {
    // The menu re-checks destroyability at click time, but this modal then sits
    // open waiting on a human: a background fetchSandboxes or another admin can
    // finish the teardown in between. Re-resolve every target against the CURRENT
    // realm list and drop the ones that no longer qualify — a realm that reached
    // Removed, or that no longer resolves to a tenant (an empty tenant header is
    // silently replaced with the caller's own, widening the blast radius).
    const realmInfo = realmIds.flatMap((id) => {
      const sandbox = getSandbox(id);

      return sandbox?.tenant && checkIsSandboxDestroyable(sandbox)
        ? [{ realmId: id, tenant: sandbox.tenant }]
        : [];
    });

    if (!realmInfo.length) {
      showError(`Teardown has already finished for the selected ${realmType}.`);
      onClose();
      return;
    }

    if (realmInfo.length < realmIds.length) {
      showError(
        `Skipped ${realmIds.length - realmInfo.length} of ${realmIds.length} ${realmType} — their teardown finished while this dialog was open.`,
      );
    }

    dispatch(
      deleteRealms({
        realmInfo,
        reasonToArchive: isRetry ? RETRY_DESTROY_REASON : DESTROY_REASON,
        onSuccess: ({ response }: any) => {
          const { data } = response || {};
          const message = data?.message || 'Successfully destroyed selected realms.';
          onDestroyRealmSuccess?.();
          showSuccess(message);
        },
        onFailure: ({ error }: any) => {
          const apiMessage = error?.data?.message || error?.message;
          const apiMessageSuffix = apiMessage ? `: ${apiMessage}` : '';
          showError(`Failed to delete all ${realmType}${apiMessageSuffix}`);
        },
      }),
    );
    onClose();
  };

  return (
    <SimpleModal
      open
      onClose={onClose}
      onConfirm={onConfirm}
      headerText={isRetry ? 'Retry Destroy' : `Destroy ${realmIds.length > 1 ? plural : singular}`}
      confirmButtonText={isRetry ? 'Retry Destroy' : 'Destroy'}
      onConfirmButtonClassNames="red-warning-button"
      Body={
        <div>
          <div>
            You are about to {isRetry ? 'retry destroying' : 'destroy'} <b>{realmIds.length}</b>{' '}
            {realmIds.length > 1 ? plural : singular}.
          </div>
          <Box
            component="ul"
            maxHeight={350}
            overflow="auto"
            sx={{
              listStyle: 'none',
              padding: 0,
              mt: 2,
              mb: 2,
            }}
          >
            {realmIds.map((realmId) => {
              const sandbox = getSandbox(realmId);
              return (
                <Box
                  component="li"
                  key={realmId}
                  sx={{
                    py: 0.75,
                    px: 0,
                    borderBottom: '1px solid',
                    borderColor: (theme) =>
                      theme.palette.mode === 'dark'
                        ? 'rgba(255, 255, 255, 0.1)'
                        : 'rgba(0, 0, 0, 0.1)',
                    '&:last-child': {
                      borderBottom: 'none',
                    },
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      fontFamily: 'monospace',
                      color: 'text.primary',
                    }}
                  >
                    {realmId || 'N/A'} • Tenant: {sandbox?.tenant || 'N/A'} • Team Name:{' '}
                    {sandbox?.team_name || 'N/A'}
                  </Typography>
                </Box>
              );
            })}
          </Box>
          <Box margin="1rem 0">
            {isRetry ? (
              <span>
                These realms are already archived. This re-runs resource teardown for realms that
                were only partially removed.
              </span>
            ) : (
              <b>Sandboxes and Subscriptions are non-recoverable after destroyed!</b>
            )}
          </Box>
        </div>
      }
    />
  );
};
